import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Video, FileText, Play, ThumbsUp, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PodcastsPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [podcasts, setPodcasts] = useState([]);
  const [title, setTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [transcript, setTranscript] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await axios.get(`${API}/podcasts`);
      setPodcasts(response.data);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    }
  };

  const submitPodcast = async () => {
    if (!title || !videoUrl) {
      toast.error('Please provide title and video URL');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/podcasts`, {
        title,
        video_url: videoUrl,
        transcript
      });

      toast.success('Podcast posted successfully!');
      setTitle('');
      setVideoUrl('');
      setTranscript('');
      fetchPodcasts();
    } catch (error) {
      toast.error('Failed to post podcast');
    } finally {
      setUploading(false);
    }
  };

  const likePodcast = async (podcastId) => {
    try {
      await axios.post(`${API}/podcasts/${podcastId}/like`);
      fetchPodcasts();
    } catch (error) {
      toast.error('Failed to like podcast');
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Cricket Podcasts</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Share and discover cricket podcasts, interviews, and commentary</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upload Form */}
          <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Post Podcast</h2>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2 block`}>Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Podcast title..."
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  data-testid="podcast-title-input"
                />
              </div>
              
              <div>
                <label className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2 block`}>Video URL</label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="YouTube, Vimeo, or direct video link..."
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  data-testid="video-url-input"
                />
              </div>
              
              <div>
                <label className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2 block`}>Transcript (Optional)</label>
                <Textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Podcast transcript or description..."
                  rows={8}
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                  data-testid="transcript-input"
                />
              </div>
              
              <Button 
                onClick={submitPodcast}
                disabled={uploading}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                data-testid="submit-podcast-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Posting...' : 'Post Podcast'}
              </Button>
            </div>
          </Card>

          {/* Podcast Feed */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Latest Podcasts</h2>
            
            {podcasts.map((podcast) => (
              <Card key={podcast.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {podcast.title}
                      </h3>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {new Date(podcast.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  {podcast.video_url && (
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      <iframe
                        src={podcast.video_url.includes('youtube') 
                          ? podcast.video_url.replace('watch?v=', 'embed/') 
                          : podcast.video_url}
                        className="w-full h-full"
                        allowFullScreen
                        title={podcast.title}
                      />
                    </div>
                  )}
                  
                  {podcast.transcript && (
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className={`text-sm font-bold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Transcript</span>
                      </div>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} whitespace-pre-wrap max-h-40 overflow-y-auto`}>
                        {podcast.transcript}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => likePodcast(podcast.id)}
                      className={`flex items-center space-x-1 ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'} transition-colors`}
                    >
                      <ThumbsUp className="w-5 h-5" />
                      <span>{podcast.likes || 0}</span>
                    </button>
                    <button className={`flex items-center space-x-1 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'} transition-colors`}>
                      <MessageCircle className="w-5 h-5" />
                      <span>{podcast.comments || 0}</span>
                    </button>
                  </div>
                </div>
              </Card>
            ))}
            
            {podcasts.length === 0 && (
              <div className="text-center py-16">
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg`}>No podcasts yet. Be the first to share!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
